import { useDailyStats, type UserDailyStatsResponseDTO } from 'app/network/daily-stats';
import { useUser } from 'app/network/user';

import {
  buildStreakDevMockStats,
  STREAK_DEV_MOCK_ENABLED,
  STREAK_DEV_MOCK_SCENARIO_DEFAULT,
  STREAK_MOCK_TOTAL_DAYS,
} from './streak-dev-mock';
import { STREAK_TOTAL_DAYS, todayDateString } from './types';

export interface StreakContributionUi {
  /** Calendar streak length from API (or mock). */
  numStreak: number;
  hasContributedToday: boolean;
  /** True once the user has started a streak but has not completed today’s action yet — gray header + header widget. */
  streakMuted: boolean;
  completedDays: number[];
  missedDays: number[];
  currentDay: number;
  totalDays: number;
  showIceCube: boolean;
  hasNeverContributed: boolean;
  effectiveDailyStats: UserDailyStatsResponseDTO | undefined;
}

export function useStreakContributionUi(): StreakContributionUi {
  const { data: user } = useUser();
  const { data: statsFromApi } = useDailyStats({ enabled: !STREAK_DEV_MOCK_ENABLED });

  const today = todayDateString();

  return (() => {
    if (STREAK_DEV_MOCK_ENABLED) {
      const scenario = STREAK_DEV_MOCK_SCENARIO_DEFAULT;
      const mockStats = buildStreakDevMockStats(scenario);
      const hasContributedToday = mockStats.actions.some((a) => a.date === today);

      const numStreak = scenario.numStreak;
      const hasNeverContributed = numStreak === 0;
      const streakMuted = numStreak > 0 && !hasContributedToday;
      const missedDays = [...scenario.missedDaySlots];
      const currentDay = scenario.currentDaySlot;
      const showIceCube = missedDays.includes(currentDay - 1);

      return {
        numStreak,
        hasContributedToday,
        streakMuted,
        completedDays: [...scenario.completedDaySlots],
        missedDays,
        currentDay,
        totalDays: STREAK_MOCK_TOTAL_DAYS,
        showIceCube,
        hasNeverContributed,
        effectiveDailyStats: mockStats,
      };
    }

    const actions = statsFromApi?.actions ?? [];
    const hasContributedToday = actions.some((a) => a.date === today);
    const numStreak = user?.numStreak ?? 0;
    const hasNeverContributed = numStreak === 0;
    const hasShared = user?.hasShared === true;
    const streakMuted = numStreak > 0 && !hasContributedToday && !hasShared;

    const useApiStreakSlots =
      user !== undefined &&
      user.hasShared !== true &&
      Array.isArray(user.streakCompletedDaySlots) &&
      Array.isArray(user.streakMissedDaySlots) &&
      typeof user.streakCurrentDaySlot === 'number';

    const completedDays: number[] = useApiStreakSlots
      ? user.streakCompletedDaySlots!
      : Array.from({ length: numStreak }, (_, i) => i + 1);
    const missedDays: number[] = useApiStreakSlots ? user.streakMissedDaySlots! : [];
    const currentDay = useApiStreakSlots ? user.streakCurrentDaySlot! : hasContributedToday ? numStreak : numStreak + 1;
    const showIceCube = missedDays.includes(currentDay - 1);

    return {
      numStreak,
      hasContributedToday,
      streakMuted,
      completedDays,
      missedDays,
      currentDay,
      totalDays: STREAK_TOTAL_DAYS,
      showIceCube,
      hasNeverContributed,
      effectiveDailyStats: statsFromApi,
    };
  })();
}
